import type { HiddenMeetings } from '@/lib/dashboard/hiddenMeetings';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

// Bumped from 1: added the required `hiddenMeetings` field, so pre-existing
// entries (which lack it) must be invalidated rather than read as valid.
const CACHE_VERSION = 2;
const STORAGE_KEY_PREFIX = 'ahoy-dashboard-prefetch-';
const COMPONENT = 'persisted-dashboard-prefetch';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface PersistedDashboardPrefetch {
  userEntityId: string | null;
  integrationEntityIds: string[];
  hiddenMeetings: HiddenMeetings;
}

function isHiddenMeetingsShape(raw: unknown): raw is HiddenMeetings {
  if (typeof raw !== 'object' || raw === null) {
    return false;
  }
  const obj = raw as Record<string, unknown>;
  return (
    Array.isArray(obj.seriesIds) &&
    obj.seriesIds.every(el => typeof el === 'string') &&
    Array.isArray(obj.meetingEntityIds) &&
    obj.meetingEntityIds.every(el => typeof el === 'string')
  );
}

function isDashboardPrefetchShape(
  raw: unknown
): raw is PersistedDashboardPrefetch {
  if (typeof raw !== 'object' || raw === null) {
    return false;
  }
  const obj = raw as Record<string, unknown>;
  if (obj.userEntityId !== null && typeof obj.userEntityId !== 'string') {
    return false;
  }
  if (!Array.isArray(obj.integrationEntityIds)) {
    return false;
  }
  if (
    !(obj.integrationEntityIds as unknown[]).every(el => typeof el === 'string')
  ) {
    return false;
  }
  return isHiddenMeetingsShape(obj.hiddenMeetings);
}

export function getPersistedDashboardPrefetch(
  orgId: number | undefined
): PersistedDashboardPrefetch | undefined {
  if (orgId === undefined) {
    return undefined;
  }
  const key = `${STORAGE_KEY_PREFIX}${orgId}`;
  return getPersistedCache<PersistedDashboardPrefetch>(
    key,
    CACHE_VERSION,
    isDashboardPrefetchShape,
    COMPONENT,
    MAX_AGE_MS
  );
}

export function setPersistedDashboardPrefetch(
  orgId: number,
  value: PersistedDashboardPrefetch
): void {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`;
  setPersistedCache<PersistedDashboardPrefetch>(
    key,
    value,
    CACHE_VERSION,
    COMPONENT
  );
}

export function clearAllPersistedDashboardPrefetch(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
